/**
 * НОМЕР ДЛЯ ЗВОНИЛКИ (`s713`, Д-13 приёмки штаба 05.10.2026).
 *
 * Слова задания: «звонилка нормализует ввод и Ctrl+V: российские 8/+7/10 цифр — в маску +7.
 * Не принимать произвольные буквы, склейку нескольких номеров, молча обрезанные значения.
 * Не превращать международный номер в российский. Сохранить нормальное редактирование и DTMF».
 *
 * ⛔ ЧТО БЫЛО. Поле стояло с заготовкой «+7» и принимало любые знаки, а на линию уходили
 * просто все цифры строки. Вставил «8 (925) 505-33-55» в поле с «+7» — уходило
 * «789255053355»: двенадцать цифр, номер не тот. Буквы и два номера подряд молча
 * «очищались» до цифр.
 *
 * ⛔ ПРАВИЛА. Одно место на набор руками, на вставку и на звонок из карточек:
 *  · +7 и десять цифр, 8 и десять цифр, 7 и десять цифр, просто десять цифр → +7 (XXX) XXX-XX-XX;
 *  · номер с плюсом и другим кодом страны остаётся международным, как набран;
 *  · буквы, посторонние знаки, второй плюс, лишние цифры — отказ со словами, поле не меняется;
 *  · без плюса и не похоже на российский — отказ: международный номер начинается с плюса;
 *  · звёздочка и решётка (тональные и служебные коды) остаются как набраны, без маски.
 */

export type OtkazNomera = 'bukvy' | 'znaki' | 'neskolko' | 'dlinnyy' | 'nuzhen_plyus';

export const SLOVA_OTKAZA: Record<OtkazNomera, string> = {
  bukvy: 'В номере не может быть букв — поле не изменено.',
  znaki: 'В номере посторонние знаки — поле не изменено.',
  neskolko: 'Похоже на несколько номеров сразу. Вставьте один.',
  dlinnyy: 'После +7 — десять цифр. Лишние цифры не приняты.',
  nuzhen_plyus: 'Не похоже на российский номер. Международный начните с плюса и кода страны.',
};

export const SLOVO_VOSMERKI = 'Первая цифра принята как выход на межгород и убрана — проверьте номер.';

export type VidNomera = 'pusto' | 'ru' | 'mir' | 'kod' | 'nepolnyy';

export interface Nomer {
  /** Что стоит в поле. */
  pole: string;
  vid: VidNomera;
  /** Что уходит на линию; `null` — звонить нельзя (номер неполный, пустой или это код). */
  nabor: string | null;
  /** Правка, сделанная за человека, — говорится словами, а не молча. */
  zametka: string | null;
}

export type ItogVvoda = { ok: true; nomer: Nomer } | { ok: false; otkaz: OtkazNomera };

const MAKS_MIR = 15;

function maskaRu(desyat: string): string {
  const a = desyat.slice(0, 3);
  const b = desyat.slice(3, 6);
  const c = desyat.slice(6, 8);
  const d = desyat.slice(8, 10);
  let s = '+7';
  if (a !== '') s += ` (${a}`;
  if (a.length === 3 && b !== '') s += ')';
  if (b !== '') s += ` ${b}`;
  if (c !== '') s += `-${c}`;
  if (d !== '') s += `-${d}`;

  return s;
}

function ru(nacionalnyy: string, zametka: string | null = null): ItogVvoda {
  const polnyy = nacionalnyy.length === 10;

  return {
    ok: true,
    nomer: {
      pole: maskaRu(nacionalnyy),
      vid: polnyy ? 'ru' : 'nepolnyy',
      nabor: polnyy ? `7${nacionalnyy}` : null,
      zametka,
    },
  };
}

/**
 * Привести строку поля (после набора или вставки) к номеру — либо отказать.
 *
 * @param syroe    строка целиком, как она стала бы после действия человека
 * @param vstavka  строка пришла вставкой целиком: десять цифр без плюса — уже российский номер
 */
export function razobratNomer(syroe: string, vstavka = false): ItogVvoda {
  const s = syroe.replace(/[\u00a0\u2009\u202f]/g, ' ').trim();

  if (s === '') return { ok: true, nomer: { pole: '', vid: 'pusto', nabor: null, zametka: null } };
  if (/\p{L}/u.test(s)) return { ok: false, otkaz: 'bukvy' };
  if (/[^0-9+*#()\-\s.]/.test(s)) return { ok: false, otkaz: 'znaki' };

  const plyusov = (s.match(/\+/g) ?? []).length;
  if (plyusov > 1 || (plyusov === 1 && !s.startsWith('+'))) return { ok: false, otkaz: 'neskolko' };

  // Тональные и служебные коды: звёздочка и решётка — как набраны, без маски и без звонка.
  if (/[*#]/.test(s)) {
    const kod = s.replace(/[^0-9*#]/g, '');
    if (plyusov > 0) return { ok: false, otkaz: 'znaki' };
    if (kod.length > 24) return { ok: false, otkaz: 'dlinnyy' };

    return { ok: true, nomer: { pole: kod, vid: 'kod', nabor: null, zametka: null } };
  }

  const cifry = s.replace(/\D/g, '');

  if (plyusov === 1) {
    if (cifry === '') return { ok: true, nomer: { pole: '+', vid: 'nepolnyy', nabor: null, zametka: null } };

    if (cifry[0] === '7') {
      const nac = cifry.slice(1);
      if (nac.length <= 10) return ru(nac);
      // «+7» и следом номер через 8 или 7 — частая вставка в поле с заготовкой «+7».
      if (nac.length === 11 && (nac[0] === '8' || nac[0] === '7')) return ru(nac.slice(1), SLOVO_VOSMERKI);

      return { ok: false, otkaz: nac.length >= 20 ? 'neskolko' : 'dlinnyy' };
    }

    if (cifry[0] === '0') return { ok: false, otkaz: 'znaki' };
    if (cifry.length > MAKS_MIR) return { ok: false, otkaz: cifry.length >= 20 ? 'neskolko' : 'dlinnyy' };

    // Международный остаётся международным: код страны не трогаем и в +7 не превращаем.
    const polnyy = cifry.length >= 8;

    return { ok: true, nomer: { pole: `+${cifry}`, vid: polnyy ? 'mir' : 'nepolnyy', nabor: polnyy ? cifry : null, zametka: null } };
  }

  // Без плюса.
  if (cifry.length === 11 && (cifry[0] === '8' || cifry[0] === '7')) return ru(cifry.slice(1));
  if (cifry.length === 10 && vstavka) return ru(cifry);
  if (cifry.length >= 20) return { ok: false, otkaz: 'neskolko' };
  if (cifry.length > 10) return { ok: false, otkaz: 'nuzhen_plyus' };

  /*
   * `s720` (приёмка 06.10.2026: «добить нормализацию номера при вводе»). Набор с клавиатуры в
   * очищенное поле раньше оставался голыми цифрами до одиннадцатой: «8925505335» выглядел готовым
   * номером и набирался как 7 892 550-53-35, а «9255053355» шёл без маски вовсе. Теперь маска
   * +7 встаёт с первой цифры — человек видит, как его цифры поняты, ещё до звонка:
   *  · первая 8 или 7 — выход на межгород (так номер и диктуют): дальше идут цифры номера;
   *  · первая 9, 3 или 4 — сразу номер (мобильные и городские коды России начинаются с них).
   * Прочее начало (0, 1, 2, 5, 6) российским номером быть не может — цифры остаются как набраны.
   * Вставка сюда не приходит: целый номер разобран выше.
   */
  if (!vstavka && (cifry[0] === '8' || cifry[0] === '7')) return ru(cifry.slice(1));
  if (!vstavka && (cifry[0] === '9' || cifry[0] === '3' || cifry[0] === '4')) return ru(cifry);

  // Набор ещё идёт: цифры как есть. Десять цифр уже можно звонить — это российский номер.
  return {
    ok: true,
    nomer: {
      pole: cifry,
      vid: cifry.length === 10 ? 'ru' : 'nepolnyy',
      nabor: cifry.length === 10 ? `7${cifry}` : null,
      zametka: null,
    },
  };
}

/**
 * Номер для звонка из карточек и журнала (там он приходит готовым, не из поля):
 * та же проверка, что у ручного набора. `null` — звонить на такое нельзя.
 */
export function naborIzGotovogo(nomer: string): string | null {
  const itog = razobratNomer(nomer, true);

  return itog.ok ? itog.nomer.nabor : null;
}

/**
 * НОМЕР ДЛЯ ПЕРЕВОДА ЗВОНКА (`s720`, остаток Д-13). Поле перевода брало «все цифры строки» мимо
 * правила: «+7 8 925 …» уходил двенадцатью цифрами. Внешний номер идёт тем же правилом, что набор;
 * короткий внутренний (5–9 цифр без плюса и знаков) — как набран: это добавочный, а не телефон.
 */
export function naborDlyaPerevoda(nomer: string): string | null {
  const s = nomer.trim();
  if (/^\d{5,9}$/.test(s)) return s;

  return naborIzGotovogo(s);
}

/**
 * ПОСЛЕДНИЙ РУБЕЖ ПЕРЕД ЛИНИЕЙ (`s720`). На линию уходит только то, что уже прошло правило:
 * 7XXXXXXXXXX, международный 8–15 цифр либо короткий внутренний. Строка с плюсом, пробелами,
 * скобками или двенадцатью цифрами на семёрку сюда попасть не должна — а попала бы, звонок не
 * начнётся (та же проверка стоит в маршруте станции и на сервере).
 */
export function godenNaLiniyu(nabor: string): boolean {
  // Служебные назначения станции (проверка связи: эхо и тон) — не телефонные номера.
  if (/^opsmind-[a-z]+$/.test(nabor)) return true;
  if (!/^\d{5,15}$/.test(nabor)) return false;

  return nabor[0] !== '7' || nabor.length === 11;
}
